import { createFileRoute, useRouter } from '@tanstack/react-router'
import { withSessionRedirect } from '../lib/require-session.ts'
import { SessionsPage } from '../components/SessionsPage.tsx'
import { useAutoRefresh } from '../lib/use-auto-refresh.ts'
import { ensureSessionLookups } from '../lib/session-lookups.ts'
import { listSessions } from '../lib/sessions-api.ts'
import { requireTenantProduct } from '../lib/tenant-products.ts'
import {
  DEFAULT_SESSION_PAGE_SIZE,
  parseSessionsSearch,
  resolveSessionsView,
  trailParam,
} from '../lib/sessions-search.ts'

export const Route = createFileRoute('/sessions')({
  validateSearch: parseSessionsSearch,
  // `session` is deliberately absent: selecting a row must not refetch
  // the list. The detail panel fetches its session by id, so it works
  // whether or not the row is on the current page.
  loaderDeps: ({ search }) => ({
    live: search.live,
    // A dep, unlike `session`: this one changes which rows the SERVER
    // returns, so the loader has to re-run for it. It is how the Projects
    // page's "Sessions for this project" link lands here — see
    // `lib/cross-links.ts`.
    projectId: search.projectId,
    pageSize: search.pageSize,
    cursors: search.cursors,
  }),
  loader: async ({ context, deps }) => {
    const view = resolveSessionsView(deps)
    // Entitlement before data. A tenant without the Sessions product is
    // refused this list with a 403, so rendering the page would mean a
    // wall of permission errors on a URL the sidebar no longer offers
    // but a bookmark still holds; the guard sends them to the landing
    // page instead. Cosmetic — the API is the one enforcing it — and a
    // failed or unauthenticated tree call is "unknown" rather than an
    // error, so the /login bounce below is still the only one.
    await requireTenantProduct(context.queryClient, 'sessions')
    // Only the session list is gated: a 401 here means no/expired session
    // (including SSR loads, which carry no cookie) and lands on /login.
    const page = await withSessionRedirect(() =>
      listSessions({
        // Passed explicitly because the SERVER default is `false` — CLI
        // and script callers keep reading the whole table — while the
        // admin wants "what is running now" first.
        liveOnly: view.liveOnly,
        // `undefined` when no project is named, which `listSessions`
        // leaves off the query string entirely.
        projectId: view.projectId,
        limit: view.pageSize,
        before: view.before,
      }),
    )
    // Cached per router (and so per SSR request) with a 5-minute stale
    // time, which is what keeps the hierarchy and the four registry lists
    // out of the 5-second poll below. See `session-lookups.ts`.
    const lookups = await ensureSessionLookups(context.queryClient)
    return { page, lookups }
  },
  component: SessionsRoute,
})

/** How often the list re-reads session status. Matches the detail
 * panel's cadence, so a session doesn't show one status in the list and
 * another in the panel beside it. Exported so tests assert against the
 * real value rather than a copy that can drift. */
export const REFRESH_MS = 5000

function SessionsRoute() {
  const { page, lookups } = Route.useLoaderData()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const router = useRouter()
  const view = resolveSessionsView(search)

  // Status is the whole point of this list — which agent is working, which
  // has stopped, which is waiting on you — and it was only ever refreshed
  // as a side effect of an operator action. An agent going busy ->
  // needs_input while you watched showed nothing until you clicked
  // something, which makes an accurate status invisible exactly when it
  // matters. The hook owns the hidden-tab behaviour.
  //
  // Every tick re-runs the loader, which is why the view below is read
  // from the URL: anything held in component state would still be here,
  // but would immediately disagree with the rows the tick just replaced.
  useAutoRefresh(REFRESH_MS)

  /** Rewrites the search params, keeping the ones this change does not
   * speak to. Spreading `search` rather than replacing it is load-bearing
   * — the old selection handler replaced the whole object, which would
   * now silently drop the filter and the page on every row click.
   *
   * `replace`, as the selection handler already did: every one of these
   * is an adjustment to the same view, and pushing twelve of them would
   * turn the browser's Back button into an undo for filter clicks
   * instead of a way off the page. The URL is still a bookmark and still
   * a paste-able deep link; it just is not a history trail. */
  function go(next: Partial<typeof search>): void {
    void navigate({ search: { ...search, ...next }, replace: true })
  }

  return (
    <SessionsPage
      sessions={page.items}
      total={page.total}
      page={view.page}
      pageSize={view.pageSize}
      liveOnly={view.liveOnly}
      projectId={view.projectId ?? null}
      // The project's own name where the lookups know it, its raw id
      // where they do not — a filter chip reading `018f…` is still
      // better than one that silently drops the project it is filtering
      // on. `names` is best-effort by contract (`session-lookups.ts`).
      projectLabel={
        view.projectId === undefined
          ? null
          : (lookups.names[view.projectId] ?? view.projectId)
      }
      onProjectIdChange={(projectId) => {
        // Same cursor reset as the live-only filter: the trail was walked
        // under the old filter, so its positions mean nothing under the
        // new one, and `total` beside it changes too.
        go({ projectId: projectId ?? undefined, cursors: undefined })
      }}
      // The cursor, not the count, decides whether Next is live: see
      // `PaginationProps.hasNextPage`.
      hasNextPage={page.nextCursor !== null}
      onLiveOnlyChange={(liveOnly) => {
        // Back to page 1: the cursor trail was walked under the old
        // filter, so its rows mean nothing under the new one — and
        // `total` changes too, which is the number beside it.
        go({ live: liveOnly ? undefined : false, cursors: undefined })
      }}
      onPageSizeChange={(pageSize) => {
        // Same reasoning: a cursor is a position in a walk of a given
        // stride, so changing the stride invalidates it. The default size
        // is left out of the URL rather than written into it — a param
        // that only ever restates the default is noise in a link someone
        // is meant to paste into a ticket.
        go({
          pageSize:
            pageSize === DEFAULT_SESSION_PAGE_SIZE ? undefined : pageSize,
          cursors: undefined,
        })
      }}
      onPageChange={(wanted) => {
        // Forward-only cursors: the trail IS the history, so the only two
        // moves are push this page's cursor and pop the last one.
        // `Pagination` never asks for a jump, and a null cursor means
        // Next is already disabled, so the guard is belt and braces.
        if (wanted > view.page) {
          if (page.nextCursor === null) return
          go({ cursors: trailParam([...view.trail, page.nextCursor]) })
          return
        }
        go({ cursors: trailParam(view.trail.slice(0, -1)) })
      }}
      projects={lookups.projects}
      agentDefs={lookups.agentDefs}
      failoverConfigs={lookups.failoverConfigs}
      names={lookups.names}
      selectedId={search.session ?? null}
      onSelect={(id) => {
        go({ session: id ?? undefined })
      }}
      onChanged={async () => {
        await router.invalidate()
      }}
    />
  )
}
